import React, { useMemo, useState } from 'react';
import { Loader2, UserRound, UserMinus } from 'lucide-react';
import { SpecialistIcon } from '../chatHome/specialistIcons';
import useAgentImage from '../../hooks/useAgentImage';
import AgentHiringModal from './AgentHiringModal';
import { useCatalogAvatars, useCatalogIcons } from '../chatHome/useCatalogAvatars';
import { TYPE } from '../../constants/typeScale';

/**
 * AGENT SEAT — the one way an agent is ever picked (owner law 2026-08-16): "with all picking of AI Agents, use
 * the same component, design how we pick our writer, don't do scroller, dropdown, etc, stay consistency."
 *
 * A seat states WHO HOLDS IT (name + what they bring), and opens the hiring room — `AgentHiringModal`, where the
 * candidate is met properly: portrait at size, their gallery, the full bio, one deliberate Recruit. That is the
 * pattern the writer's seat proved; this extracts it so the director, the composer and every craft lead on the
 * crew are picked exactly the same way. A surface with several roles renders several SEATS — never a rail of
 * faces, a dropdown, a chip row or a grid of names, all of which skip the audition that is the whole point.
 *
 * Presentational: the recruit itself is the caller's (the Studio owns the crew chat).
 */
export interface SeatCandidate {
  agentId: string;
  name: string;
  /** The style/craft they bring ("Kinetic short-form") — shown once they hold the seat. */
  flavor?: string;
  /** One line on what they are good at — what a creator actually decides on. */
  hint?: string;
  /** Overrides what the hiring room says this candidate brings (defaults to flavor + hint). */
  note?: string;
}

export interface AgentSeatProps {
  /** The quiet label above the row: "Your director". */
  label: string;
  /** The empty seat's invitation, and the hiring room's title: "Choose your director". */
  seatTitle: string;
  /** This seat's bench, in display order. */
  candidates: SeatCandidate[];
  /** Who holds it right now, if anyone. */
  currentId?: string | null;
  /**
   * Put this agent on the production. Rejecting surfaces the reason on the seat.
   *
   * `replacesAgentId` is who currently holds the seat, passed whenever the pick is a CHANGE rather than a
   * first hire — the seat is the only place that knows, and without it recruiting silently added a second
   * holder of the same craft and the seat never changed (BUGS.md 2026-09-13).
   */
  onRecruit: (agentId: string, replacesAgentId?: string) => void | Promise<void>;
  /**
   * Take the current holder OFF the production. Optional: a surface that cannot dismiss simply does not offer
   * it. The crew chat updates with the seat, because a roster that disagrees with the seats is the thing this
   * whole area keeps getting wrong.
   */
  onDismiss?: (agentId: string) => void | Promise<void>;
  verb?: string;
  hiredLabel?: string;
  testId?: string;
  /**
   * A one-line CHIP instead of a full-width card (owner 2026-08-23: on Story *"'Your writer' should be a small
   * icon and above the text area"*). Same seat, same hiring room — only the footprint changes, so a page whose
   * subject is the writing does not spend a card-sized block on who is doing it.
   */
  compact?: boolean;
}

const AgentSeat: React.FC<AgentSeatProps> = ({
  label, seatTitle, candidates, currentId, onRecruit, onDismiss, verb = 'Recruit', hiredLabel = 'On your team', testId, compact,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const avatars = useCatalogAvatars();   // agent id -> portrait; cached, so a chip costs no extra round trip
  const icons = useCatalogIcons();       // agent id -> their own Lucide glyph, which every agent HAS
  const current = candidates.find((c) => c.agentId === currentId) || null;
  const held = current ? [current.flavor, current.hint].filter(Boolean).join(' · ') : '';

  // What the hiring room says each candidate brings — the craft note the catalog carries, which a public bio
  // cannot know ("Kinetic short-form · Fast, punchy cutting for the feed").
  const notes = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of candidates) {
      const note = c.note ?? [c.flavor, c.hint].filter(Boolean).join(' · ');
      if (note) map[c.agentId] = note;
    }
    return map;
  }, [candidates]);

  const letGo = async () => {
    if (!current || !onDismiss) return;
    setBusy(true); setError('');
    try {
      await onDismiss(current.agentId);
    } catch (e) {
      // The server's copy is the honest one — it refuses to empty a crew, and says why.
      setError((e as Error)?.message || 'Could not take them off the crew just now.');
    } finally {
      setBusy(false);
    }
  };

  const recruit = async (agentId: string) => {
    setBusy(true); setError('');
    try {
      // A pick on a filled seat is a SWAP. Picking the person already there is a no-op, not a replacement.
      await onRecruit(agentId, currentId && currentId !== agentId ? currentId : undefined);
    } catch (e) {
      // The server's copy is the honest one (a full crew, a plan limit) — a generic line hides why.
      setError((e as Error)?.message || 'Could not bring them on board just now.');
    } finally {
      setBusy(false);
    }
  };

  const hiringRoom = (
    <AgentHiringModal
      open={open}
      onClose={() => setOpen(false)}
      candidateIds={candidates.map((c) => c.agentId)}
      currentId={currentId || null}
      title={seatTitle}
      verb={verb}
      hiredLabel={hiredLabel}
      notes={notes}
      onHire={(a) => { void recruit(a.id); }}
    />
  );

  /**
   * The seat's FACE (BUGS.md 2026-08-30: every filled seat showed the same anonymous silhouette).
   *
   * Two faults, one symptom. The portrait was used RAW as an `<img src>`, while every other surface that shows
   * an agent picture passes it through `useAgentImage` — which exists because these values arrive as a Storage
   * path or as one of our tokenless public URLs that 403 for a bare `<img>` (it sends no credentials). And the
   * fallback was a single generic `UserRound`, so once the portrait was missing — which is the NORMAL case,
   * since `avatar_image` is an admin overlay the profile catalog never sets — every seat on the page looked
   * identical, and a creator could not tell who they had hired.
   *
   * So: resolve the portrait properly, and fall back to the agent's OWN catalog icon. `UserRound` now means
   * only what it should — nobody is in this seat yet.
   */
  const portrait = useAgentImage(current ? avatars[current.agentId] : null);
  const face = busy
    ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-secondary-400" aria-hidden />
    : current && portrait
      ? <img src={portrait} alt={current.name} referrerPolicy="no-referrer" className="h-5 w-5 shrink-0 rounded-full object-cover" />
      : current
        ? <SpecialistIcon name={icons[current.agentId] || 'bot'} className="h-4 w-4 shrink-0 text-secondary-500 dark:text-secondary-300" />
        : <UserRound className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />;

  if (compact) {
    return (
      <div className="inline-flex flex-col items-start">
        <button
          type="button"
          data-testid={testId}
          onClick={() => setOpen(true)}
          disabled={busy}
          /* Deliberately the SAME chip as the plot-library trigger beside it, so the row above the idea reads
             as one set of controls rather than two unrelated widgets. */
          title={current ? `${label}: ${current.name}` : seatTitle}
          className={`inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 bg-white px-2.5 py-1
                     ${TYPE.control} text-secondary-600 transition-colors hover:border-secondary-400
                     disabled:opacity-60 dark:border-secondary-600 dark:bg-secondary-900 dark:text-secondary-300
                     dark:hover:border-secondary-500`}
        >
          {face}
          {current
            ? <span className="font-medium text-secondary-900 dark:text-white">{current.name}</span>
            : seatTitle}
        </button>
        {error && <p className={`mt-1 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>{error}</p>}
        {hiringRoom}
      </div>
    );
  }

  return (
    <div>
      <p className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>
        {label}
      </p>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        disabled={busy}
        className="mt-1.5 flex min-h-[44px] w-full items-center gap-3 rounded-xl border border-secondary-200 px-3.5 py-2.5
                   text-left transition-colors hover:border-secondary-400 disabled:opacity-60
                   dark:border-secondary-700 dark:hover:border-secondary-500"
      >
        <span className="min-w-0 flex-1">
          <span className={`block ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>
            {current ? current.name : seatTitle}
          </span>
          <span className={`block truncate ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
            {current ? (held || hiredLabel) : 'Meet the candidates and pick who joins your team'}
          </span>
        </span>
        {face}
      </button>

      {/*
        LEAVING IS PART OF HIRING (owner 2026-09-13: the crew chat must follow who is actually on the
        production). A seat that can only ever be filled quietly accumulates people: before this the only way
        to change your mind was to hire somebody else, and even that used to leave the first one in the room.
        Quiet by design — it sits under the seat rather than on it, because changing your mind is rarer than
        making it up.
      */}
      {current && onDismiss && (
        <button
          type="button"
          onClick={() => void letGo()}
          disabled={busy}
          data-testid={testId ? `${testId}-dismiss` : undefined}
          className={`mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${TYPE.meta} text-secondary-500
                     transition-colors hover:bg-secondary-100 hover:text-secondary-800 disabled:opacity-40
                     dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100`}
        >
          <UserMinus className="h-3 w-3" aria-hidden /> Take {current.name} off this production
        </button>
      )}

      {/* MONOCHROME, including failure (owner 2026-08-23: "dnt use red"). Colour is for state we render as
          state; a recruit that did not happen is said in words, in the same ink as everything else. */}
      {error && <p className={`mt-1 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

      {hiringRoom}
    </div>
  );
};

export default AgentSeat;
